import kebabCase from 'lodash/kebabCase';
import { feedback } from 'app_honda_cars/utils/feedback';
import analyticsHelper from 'app_honda_cars/utils/analyticsHelper';
import frontendUtils from 'honda_cars_ecux/utils/frontendUtils';

/**
 * Sub-controller for the "View Brochure" flyout view.
 *
 * Owns the in-flyout brochure-request form: validates contact details, POSTs to
 * `Page-SubmitBrowcherLead` with a `{ PreferredModels[], ContactDetails, possiblePurchase }`
 * payload, and on success swaps the flyout to the sibling `download-brochure` view
 * (wiring the fallback CTA href when that button is in the DOM).
 *
 * The "Your selected Honda Centre" panel is rendered by the shared
 * `components/selectedHondaCentre` include and refreshes itself.
 */
export const viewBrochure = {
  /**
   * Bootstrap the view-brochure view. Idempotent; safe to call once at boot.
   */
  viewBrochureView() {
    this.helpers.bootstrap();
  },

  /**
   * Controller / state container. Skipped by `processInclude` (not a function).
   */
  helpers: {
    /**
     * @type {boolean}
     */
    isSelected: false,

    /**
     * @type {boolean}
     */
    isWired: false,

    /**
     * Whether the brochure lead form has already been submitted successfully. When `true`, opening
     * the brochure re-shows the `download-brochure` result view instead of the form. Persists across
     * variant changes (the brochure link is master-level, not variant-specific) and is only cleared
     * on page reload (in-memory only).
     *
     * @type {boolean}
     */
    hasSubmitted: false,

    /**
     * @type {{
     *   view: HTMLElement | null,
     *   modal: HTMLElement | null,
     *   form: HTMLFormElement | null,
     *   downloadBtn: HTMLAnchorElement | null,
     *   toastContainer: HTMLElement | null
     * }}
     */
    elements: {
      view: null,
      modal: null,
      form: null,
      downloadBtn: null,
      toastContainer: null
    },

    /**
     * Subscribe to `product:afterAttributeSelect` (variant changes), `amb:modal-view-change`
     * (redirect the form to the result view once submitted) and `amb:modal-view-changed`
     * (lazy-wire on first show of this view).
     */
    bootstrap() {
      $('body').on('product:afterAttributeSelect', (...args) => this.afterAttributeSelect(...args));

      // Once the lead form has been submitted for the current variant, any attempt to open the
      // `view-brochure` form (e.g. re-opening from the menu after closing the flyout) is redirected
      // to the persisted `download-brochure` result view so the user can grab the PDF again.
      document.body.addEventListener('amb:modal-view-change', event => {
        if (event.detail?.view !== 'view-brochure' || !this.hasSubmitted) {
          return;
        }

        const modal = event.target?.setView ? event.target : this.elements.modal;

        if (typeof modal?.setView !== 'function') {
          return;
        }

        event.preventDefault();
        modal.clearHistory();
        modal.setView('download-brochure');
      });

      document.body.addEventListener('amb:modal-view-changed', event => {
        if (event.detail?.view !== 'view-brochure') {
          return;
        }

        this.wire();
      });
    },

    /**
     * Resolve and cache the view's DOM refs into `this.elements`. Lazy because
     * `afterAttributeSelect` can fire before the user has ever opened the flyout.
     *
     * @returns {boolean} `true` once the view + form + submit button are present in the DOM.
     */
    selectElements() {
      if (this.isSelected) {
        return true;
      }

      const view = document.querySelector('.configurator-modal .modal-view[view="view-brochure"]');

      if (!view) {
        return false;
      }

      const form = view.querySelector('#view-brochure-form');
      const submitButton = view.querySelector('.view-brochure-btn');

      if (!form || !submitButton) {
        return false;
      }

      const modal = view.closest('.configurator-modal');

      this.elements.view = view;
      this.elements.form = form;
      this.elements.modal = modal;
      this.elements.downloadBtn = modal?.querySelector('.modal-view[view="download-brochure"] .btn-download') || null;
      this.elements.toastContainer = view.querySelector('.toast-container');

      this.isSelected = true;

      return true;
    },

    /**
     * Resolve element refs and attach DOM listeners. No-op after the first successful run.
     *
     * @returns {boolean} `true` once wiring has succeeded.
     */
    wire() {
      if (this.isWired) {
        return true;
      } else if (!this.selectElements()) {
        return false;
      }

      this.elements.form.addEventListener('submit', event => this.onSubmit(event));

      $(this.elements.view).on('shown.bs.collapse', (event) => this.onCollapseShown(event));
      this.isWired = true;

      return true;
    },

    /**
     * Mirror the active variant's `customProps` into the form's hidden inputs
     * (`Model` / `Year` / `Door` / `Grade`) so `buildPayload()` always serialises the freshest
     * variant state. `Colour` keeps its legacy `"String"` placeholder for API parity.
     *
     * @param {JQuery.Event} event Unused; required by the jQuery handler contract.
     * @param {{ data: { product: { id: string, customProps: Record<string, string> } } }} response
     */
    afterAttributeSelect(event, { data }) {
      if (!this.selectElements()) {
        return;
      }

      const form = this.elements.form;
      const props = (data && data.product && data.product.customProps) || {};

      [
        ['Model', props.Model],
        ['Year', props.FullYear],
        ['Door', props.Doors],
        ['Grade', props.grade],
        ['Colour', props.color]
      ].forEach(([name, value]) => {
        const control = form.elements[name];

        if (control instanceof HTMLSelectElement) {
          control.querySelectorAll('option').forEach(option => option.toggleAttribute('selected', option.value === value));
        } else if (control) {
          control.value = value || '';
        }
      });
    },

    /**
     * @param {JQuery.EventBase} event
     */
    onCollapseShown(event) {
      event.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    },

    /**
     * POST to `Page-SubmitBrowcherLead` and dispatch to `onSuccess` / `notify` based on the
     * response.
     *
     * @param {SubmitEvent} event
     */
    onSubmit(event) {
      event.preventDefault();

      const url = this.elements.form.getAttribute('action');

      if (!url) {
        return;
      }

      const $view = $(this.elements.view).parent();

      $.ajax({
        url,
        type: 'post',
        dataType: 'json',
        contentType: 'application/json',
        data: JSON.stringify(this.buildPayload()),
        beforeSend: () => $view.spinner().start(),
        success: response => {
          if (!response?.error && Array.isArray(response?.brochureLinks)) {
            this.onSuccess(response.brochureLinks);
          } else {
            this.notify(response?.message);
          }
        },
        error: jqXHR => {
          this.notify(jqXHR?.responseJSON?.message);
        },
        complete: () => $view.spinner().stop()
      });
    },

    /**
     * Build the AJAX payload — fully client-side (mirrors `sidebar.js`). `PreferredModels[0]`
     * comes from the hidden inputs (kept fresh by {@link afterAttributeSelect});
     * `AddressPostcode` / `DealerId` / `expertId` come from the selected Honda Centre
     * hidden inputs. The JSON key is `expertId`; the input name stays `ExpertId`.
     *
     * @returns {Record<string, unknown>}
     */
    buildPayload() {
      const form = this.elements.form;
      const formData = new FormData(form);

      return {
        PreferredModels: [
          {
            Model: formData.get('Model') || '',
            Year: formData.get('Year') || '',
            Grade: formData.get('Grade') || '',
            Door: formData.get('Door') || '',
            Colour: formData.get('Colour') || '',
            productId: formData.get('productId') || ''
          }
        ],
        ContactDetails: {
          Title: formData.get('Title') || '',
          FirstName: formData.get('FirstName') || '',
          Surname: formData.get('LastName') || '',
          Email: formData.get('Email') || '',
          Mobile: formData.get('Phone') || '',
          PrivacyConsent: !!formData.get('PrivacyConsent') || false,
          AddressPostcode: formData.get('AddressPostcode') || '',
          DealerId: formData.get('DealerId') || '',
          expertId: formData.get('ExpertId') || ''
        },
        possiblePurchase: formData.get('possiblePurchase') || '',
        MetaEventId: window.sidenavBrochureUUID || frontendUtils.generateUUID(),
        GACLIENTID: analyticsHelper.getGAClientId(),
        GATRACKID: analyticsHelper.getGATrackId(),
        GAUSERID: analyticsHelper.getGAUserId(),
        GUID: analyticsHelper.getGAGUID()
      };
    },

    /**
     * Swap the flyout to `download-brochure` after a successful lead submit. Clears history so
     * Back is hidden (shared chrome: `[data-history="0"]` / non-empty active header) — Close
     * is the way out, not return-to-form. When the fallback `.btn-download` is in the DOM
     * (no merch `DownloadBrochureContent`), point it at the first usable PDF URL.
     *
     * @param {Array<{ BrochurePDFLink: string, ProductName: string }>} links
     */
    onSuccess(links) {
      // Bridge to int_gtm for the brochure form `complete` step (schema lives in brochureEvents.js).
      // Fired first so it reports on every successful submit and reads the form before the view swaps.
      $('body').trigger('gtm:event', {
        callback: 'configuratorBrochureFormComplete',
        data: $(this.elements.form)
      });

      const modal = this.elements.modal;

      if (typeof modal?.setView !== 'function') {
        return;
      }

      const link = (links || []).find(item => item?.BrochurePDFLink && item.BrochurePDFLink !== 'N/A');
      const downloadBtn = this.elements.downloadBtn;

      if (link && downloadBtn) {
        downloadBtn.href = link.BrochurePDFLink;
        downloadBtn.download = `${kebabCase(link.ProductName || '')}-brochure.pdf`;
        downloadBtn.target = '_blank';
        downloadBtn.rel = 'noopener';
      }

      modal.clearHistory();
      modal.setView('download-brochure');

      // Remember the successful submit so re-opening the brochure (after closing the flyout, or
      // after changing variant) shows this result view again instead of the form. Only reset on
      // page reload — the brochure link is master-level, so it stays valid across variant changes.
      this.hasSubmitted = true;
    },

    /**
     * Show the AJAX error message via the shared {@link feedback.toast} helper.
     * Falls back to the toast-container's `data-error-message` when the response has none.
     *
     * @param {string} [message]
     */
    notify(message) {
      const text = message || this.elements.toastContainer?.dataset.errorMessage;

      if (!text) {
        return;
      }

      feedback.toast({
        message: text,
        toastClass: 'toast-error',
        delay: 8000,
        container: this.elements.toastContainer
      });
    }
  }
};
